import React, { useEffect, useState, useRef } from 'react';
import { Contact, Item, PaymentLines } from '../../types';
import { ItemPicker } from '../masterdata/components/ItemPicker';
import NumberInput from '../widgets/components/NumberInput';
import { TaxPicker } from '../masterdata/components/TaxPicker';
import DiscountInput from '../widgets/components/DiscountInput';
import { PaymentConfig } from './TxnConfig';
import { CurrencyNumberOutput } from '../widgets/components/FormattedOutputs';
import { displayDate } from '../utils/MiscFunctions';

type FieldValue = { field: keyof PaymentLines, value: string | number | boolean, isDiscountPct?: boolean };

const EditableItemTable: React.FC<{ paymentConfig?: PaymentConfig, paymentLiness: PaymentLines[], contact: Contact, setPaymentLiness: (items: PaymentLines[]) => void, hasErrors: (errorFlag: boolean) => void }> =
  ({ paymentConfig, contact, paymentLiness, setPaymentLiness, hasErrors }) => {
    const itemPickerRef = useRef<HTMLInputElement>(null);
    const [addingDescription, setAddingDescription] = useState<{ [key: number]: boolean }>({});
    const [errors, setErrors] = useState<{ [key: number]: string }>({});
    useEffect(() => {
      if (paymentLiness.length === 0) {

      } else {
        setTimeout(() => {
          itemPickerRef.current?.focus();
        }, 0);
      }
    }, [paymentLiness]);

    const handleInputChange = (index: number, field: keyof PaymentLines | FieldValue[], value?: string | number | boolean, isDiscountPct?: boolean) => {
      const updatedPaymentLiness = [...paymentLiness];

      const updateFieldValue = (field: keyof PaymentLines, value: string | number | boolean, isDiscountPct?: boolean) => {
        const existingItem = updatedPaymentLiness[index];
        let updatedItem = { ...existingItem };

        updatedItem = {
          ...existingItem,
          [field]: value as number | string | boolean,
        };

        // Function to calculate the discount amount
        const calculateDiscount = (itemRate: number, itemQty: number, discountAmt: number, isDiscountPct: boolean) => {
          return isDiscountPct ? (itemRate * itemQty * (discountAmt / 100)) : discountAmt;
        };

        updatedPaymentLiness[index] = updatedItem;
      };

      if (Array.isArray(field)) {
        field.forEach(({ field: f, value: v, isDiscountPct: idp }) => updateFieldValue(f, v, idp));
      } else {
        updateFieldValue(field, value!, isDiscountPct);
      }

      setPaymentLiness(updatedPaymentLiness);
    };

    const handleItemChange = (index: number, item: PaymentLines) => {
      const updatedPaymentLiness = [...paymentLiness];
      handleInputChange(index, [
        { field: 'allocAmt', value: item.allocAmt ?? '' },
        // { field: 'taxPct', value: item.tax?? '' }, //TODO get Taxes fomr Id
      ]);
    };

    const handleAddDescriptionClick = (index: number) => {
      setAddingDescription((prev) => ({
        ...prev,
        [index]: !prev[index],
      }));
    };

    const handleDescriptionChange = (index: number, value: string) => {

    };


    const handleTaxChange = (index: number, taxId: string, taxPct: number) => {

    };

    useEffect(() => {
      if (Object.keys(errors).length > 0) {
        hasErrors(true)
      } else {
        hasErrors(false)
      }
    }, [errors])

    const handleChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseFloat(e.target.value);
      const dueAmt = paymentLiness[index].dueAmt || 0;

      if ((value - dueAmt) > 0.1) {
        // Set an error message if the value exceeds due amount
        setErrors((prevErrors) => ({
          ...prevErrors,
          [index]: `${'Amount cannot exceed'.toTranslatedDisplay()} ${dueAmt}`,
        }));
      } else {
        // Clear the error if the input is valid
        setErrors((prevErrors) => {
          const { [index]: removedError, ...restErrors } = prevErrors;
          return restErrors;
        });

        // Proceed with the input change

      }
      handleInputChange(index, 'allocAmt', value);
    };

    // Add these state variables after the existing useState declarations:
    const [addingNotes, setAddingNotes] = useState<{ [key: number]: boolean }>({});

    // Add these handler functions after the existing handlers:
    const handleAddNotesClick = (index: number) => {
      setAddingNotes((prev) => ({
        ...prev,
        [index]: !prev[index],
      }));
    };

    const handleNotesChange = (index: number, value: string) => {
      const updatedPaymentLiness = [...paymentLiness];
      updatedPaymentLiness[index] = {
        ...updatedPaymentLiness[index],
        notes: value,
      };
      setPaymentLiness(updatedPaymentLiness);
    };

    // Optional: Add hoverIndex state if not already present (add with other useState declarations):
    const [hoverIndex, setHoverIndex] = useState<number | null>(null);

    const newDate = new Date()
    return (
      <div>
        <table className="w-100 table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3">
          <thead className="fw-bold text-muted bg-light">
            <tr>
              <th className="p-2 ">{paymentConfig?.contactTxnType} No</th>
              <th className="ph-4">{'Date'.toTranslatedDisplay()}</th>
              <th className="me-2 ph-2 text-center">{'Total Amount'.toTranslatedDisplay()}</th>
              <th className="me-2 ph-2 text-center">{'Due Amount'.toTranslatedDisplay()}</th>
              <th className="me-2 ph-2 text-center">{'Payment Amount'.toTranslatedDisplay()}</th>
            </tr>
          </thead>
          <tbody>
            {paymentLiness.map((paymentLine, index) => (
              <tr key={paymentLine.txnId}>
                <td className='align-center w-25 p-3'>
                  <h4> {paymentLine.txnNo} </h4>
                  {(paymentLine.notes && !addingNotes[index]) ? (
                    <div
                      className="position-relative d-inline-block"
                      onMouseEnter={() => setHoverIndex && setHoverIndex(index)}
                      onMouseLeave={() => setHoverIndex && setHoverIndex(null)}
                    >
                      <span
                        className="badge badge-secondary ms-2 mt-1 text-wrap text-start lh-sm"
                        onClick={() => handleAddNotesClick(index)}
                        style={{ cursor: 'pointer' }}
                      >
                        {paymentLine.notes}
                      </span>

                      {hoverIndex === index && (
                        <i
                          className="bi bi-pencil position-absolute"
                          style={{ right: '-20px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }}
                        />
                      )}
                    </div>
                  ) : (
                    <>
                      {!addingNotes[index] && (
                        <div className="mt-2">
                          <a
                            href="#"
                            className="p-2 fw-bold text-primary"
                            onClick={(e) => {
                              e.preventDefault();
                              handleAddNotesClick(index);
                            }}
                          >
                            {'Add Note'.toTranslatedDisplay()}
                          </a>
                        </div>
                      )}
                      {addingNotes[index] && (
                        <textarea
                          value={paymentLine.notes || ''}
                          onChange={(e) => handleNotesChange(index, e.target.value)}
                          className="form-control mt-1"
                          placeholder={'Enter notes'.toTranslatedDisplay()}
                          style={{ width: '100%' }}
                        />
                      )}
                    </>
                  )}
                </td>
                <td className='align-center'>
                  <h5>
                    {paymentLine.txnDate && !isNaN(new Date(paymentLine.txnDate).getTime())
                      ? displayDate(new Date(paymentLine.txnDate))
                      : 'No Date'}
                  </h5>
                </td>
                <td className="text-center">
                  <h5> {(paymentLine.totalAmt || 0).toFixed(2)}   </h5>
                </td>
                <td className="text-center">
                  <h5> {(paymentLine.dueAmt || 0).toFixed(2)} </h5>
                </td>
                <td className='align-center'>
                  <NumberInput name={'paymentAmount'} value={paymentLine.allocAmt}
                    showCurrency
                    error={errors[index]}
                    currId={contact.currId}
                    onChange={(e) => handleChange(index, e)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

export default EditableItemTable;
